import { isMobileDevice as isMobile } from '@account-service/core'
import type {
  DappMetadata,
  EIP1193Provider,
  InitialData,
  RequestArguments,
} from '@roninnetwork/wallet-sdk'
import {
  EIP1193Event,
  extractDappMetadata,
  isMobileWebview,
  RoninConnector,
  RoninMobileConnector,
  WCEvent,
} from '@roninnetwork/wallet-sdk'
import { EventEmitter } from 'events'
import isEmpty from 'lodash.isempty'

export const SDK_ERRORS = {
  NoDappMetadata: 'dappMetadata must be provided',
  NoWCProjectId: 'walletConnectProjectId must be provided',
  InternalError: 'Internal Error',
  ProviderNotReady: 'Provider is not ready',
  NotInitialized: 'Not initialized',
}

export declare interface WalletSDK {
  on(event: WCEvent.DISPLAY_URI, listener: (uri: string) => void): this
  on(
    event: EIP1193Event.CHAIN_CHANGED,
    listener: (chainId: number) => void,
  ): this
  on(
    event: EIP1193Event.ACCOUNTS_CHANGED,
    listener: (account: string) => void,
  ): this
}

type RoninSDKOptions = {
  dappMetadata?: DappMetadata
  mobileOptions?: {
    defaultChainId?: number
    walletConnectProjectId: string
  }
}

export class WalletSDK extends EventEmitter {
  private readonly options: RoninSDKOptions

  private mobileConnector?: RoninMobileConnector

  private injectedConnector?: RoninConnector

  private provider?: EIP1193Provider

  constructor(options: RoninSDKOptions = {}) {
    super()
    const { dappMetadata } = options
    if (!dappMetadata) options.dappMetadata = extractDappMetadata()
    if (!options.dappMetadata) throw new Error(SDK_ERRORS.NoDappMetadata)
    this.options = options
  }

  private checkProviderStatus() {
    if (!this.provider) throw new Error(SDK_ERRORS.ProviderNotReady)
  }

  getProvider(): EIP1193Provider | undefined {
    return this.provider
  }

  getChainId(): Promise<number | string> {
    this.checkProviderStatus()
    return this.provider!.request<number | string>({ method: 'eth_chainId' })
  }

  getAccounts(): Promise<string[]> {
    this.checkProviderStatus()
    return this.provider!.request<string[]>({ method: 'eth_accounts' })
  }

  requestAccounts(): Promise<string[]> {
    this.checkProviderStatus()
    return this.provider!.request<string[]>({ method: 'eth_requestAccounts' })
  }

  request(args: RequestArguments): Promise<unknown> {
    this.checkProviderStatus()
    return this.provider!.request(args)
  }

  hasWCSession() {
    return !isEmpty(this.mobileConnector?.ethProvider?.session)
  }

  private initializeInjectedSetup() {
    if (!isMobile() || isMobileWebview())
      this.injectedConnector = new RoninConnector()
  }

  private async initializeMobileSetup() {
    const { dappMetadata, mobileOptions } = this.options

    if (!dappMetadata) throw new Error(SDK_ERRORS.InternalError)

    if (mobileOptions) {
      const { walletConnectProjectId } = mobileOptions

      if (!walletConnectProjectId) throw new Error(SDK_ERRORS.NoWCProjectId)

      this.mobileConnector = new RoninMobileConnector()

      await this.mobileConnector.init({
        autoHandleDisplayUri: false,
        projectId: walletConnectProjectId,
        clientMeta: {
          name: dappMetadata.name,
          description: dappMetadata.description ?? '',
          url: dappMetadata.url,
          icons: dappMetadata.icon ? [dappMetadata.icon] : [],
        },
      })
    }
  }

  private setupInjectedEventListeners() {
    if (!this.injectedConnector) return

    const chainChangedEventHandler = (chainId: number) => {
      this.emit(EIP1193Event.CHAIN_CHANGED, chainId)
    }
    const accountsChangedEventHandler = (accounts: string[]) => {
      this.emit(EIP1193Event.ACCOUNTS_CHANGED, accounts[0])
    }

    this.injectedConnector.addListener(
      EIP1193Event.CHAIN_CHANGED,
      chainChangedEventHandler,
    )
    this.injectedConnector.addListener(
      EIP1193Event.ACCOUNTS_CHANGED,
      accountsChangedEventHandler,
    )
  }

  async connectInjected(options?: { silent?: boolean }): Promise<InitialData> {
    options = options ?? { silent: false }

    this.initializeInjectedSetup()

    if (!this.injectedConnector) throw new Error(SDK_ERRORS.NotInitialized)

    this.setupInjectedEventListeners()

    const initialData = await this.injectedConnector.connect()
    this.provider = this.injectedConnector?.provider

    if (options.silent) return initialData

    const accounts = await this.requestAccounts()

    return {
      ...initialData,
      accounts,
    }
  }

  private setupMobileEventListeners(options?: { silent?: boolean }) {
    options = options ?? { silent: false }

    if (!this.mobileConnector) return

    const connectEventHandler = () => {
      this.provider = this.mobileConnector?.provider

      const sessionChangedEventHandler = ({
        params: {
          event: { name, data },
        },
      }: any) => {
        switch (name) {
          case EIP1193Event.ACCOUNTS_CHANGED: {
            const chainId = data?.[0]?.split?.(':')?.[1]
            const account = data?.[0]?.split?.(':')?.[2]
            this.emit(EIP1193Event.CHAIN_CHANGED, chainId)
            this.emit(EIP1193Event.ACCOUNTS_CHANGED, account)
            break
          }

          default:
            break
        }
      }

      this.provider?.on(WCEvent.SESSION_EVENT, sessionChangedEventHandler)
    }

    const displayUriEventHandler = (uri: string) => {
      if (options?.silent) return
      this.emit(WCEvent.DISPLAY_URI, uri)
    }

    this.mobileConnector.addListener(EIP1193Event.CONNECT, connectEventHandler)
    this.mobileConnector.addListener(
      WCEvent.DISPLAY_URI,
      displayUriEventHandler,
    )
  }

  async connectMobile(options?: { silent?: boolean }): Promise<InitialData> {
    options = options ?? { silent: false }
    const defaultChainId = this.options.mobileOptions?.defaultChainId ?? 2020

    await this.initializeMobileSetup()

    if (!this.mobileConnector) throw new Error(SDK_ERRORS.NotInitialized)

    this.setupMobileEventListeners(options)

    const initialData = await this.mobileConnector.connect()
    this.provider = this.mobileConnector.provider
    this.mobileConnector.setChainId(defaultChainId)
    this.emit(EIP1193Event.CHAIN_CHANGED, defaultChainId)

    return initialData
  }

  async disconnect(options?: { deepClear?: boolean }): Promise<void> {
    options = options ?? { deepClear: false }
    this.mobileConnector?.removeAllListeners()
    this.injectedConnector?.removeAllListeners()

    if (options.deepClear) {
      this.provider?.disconnect()
      await this.mobileConnector?.disconnect()
      await this.injectedConnector?.disconnect()
    }

    this.provider = undefined
  }
}
